import React, { useMemo } from 'react';
import { Button, Collapse, Descriptions, Empty, Space, Tag, Typography } from 'antd';
import type { Application } from '../../types';
import type { WorkspaceTask } from '../../store/api/workspaceApi';
import TaskCard from '../tasks/TaskCard';
import { StatusCell } from '../../ui';
import {
  mapApplicationIncomeObligationToBlockItem,
  projectApplicationClientSettlement,
  projectObligationClientPayments,
  resolveApplicationFinanceTaskAmount,
  sortedClientIncomeObligations,
} from '../../utils/applicationClientSettlementProjection';
import { FinanceSourceDataHint } from '../../utils/financeDomainUi';
import { projectApplicationLinkedRoutes } from '../../utils/applicationContextDrawerProjection';
import { ObligationPaymentsBlock } from '../../pages/finance/FinanceRouteModal';

const { Text, Title } = Typography;

function formatRub(amount: number | null | undefined): string {
  const n = Number.isFinite(Number(amount)) ? Math.round(Number(amount)) : 0;
  return `${n.toLocaleString('ru-RU')} ₽`;
}

export interface ApplicationContextDrawerPanelProps {
  application: Application;
  directionLine: string;
  drawerOperationalTasks: WorkspaceTask[];
  drawerFinanceTasks: WorkspaceTask[];
  overdueFinanceInContext: boolean;
  overdueOperationalInContext: boolean;
  linkedRoutes: Array<{ id: number; routeId: number; route?: { number?: string | null } | null }>;
  onInlineTaskAction: (task: WorkspaceTask) => void;
  onClose: () => void;
  onOpenFullCard: () => void;
  onSwitchToRoute?: (routeId: number) => void;
  onOpenRoute?: (routeId: number) => void;
}

export const ApplicationContextDrawerPanel: React.FC<ApplicationContextDrawerPanelProps> = ({
  application,
  directionLine,
  drawerOperationalTasks,
  drawerFinanceTasks,
  overdueFinanceInContext,
  overdueOperationalInContext,
  linkedRoutes: _linkedRoutesLegacy,
  onInlineTaskAction,
  onClose,
  onOpenFullCard,
  onSwitchToRoute,
  onOpenRoute,
}) => {
  const headerNumber = application.internalNumber?.trim() || `№${application.id}`;
  const clientSettlement = useMemo(() => projectApplicationClientSettlement(application), [application]);
  const clientObligations = useMemo(() => sortedClientIncomeObligations(application), [application]);
  const linkedRoutes = useMemo(() => projectApplicationLinkedRoutes(application), [application]);
  const taskPresentationContext = useMemo(() => {
    const client =
      application.client?.shortName?.trim() || application.client?.name?.trim() || null;
    if (!client) return undefined;
    return {
      clientShortNameByApplicationId: new Map([[application.id, client]]),
    };
  }, [application]);

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <div>
        <Title level={5} style={{ margin: 0 }}>
          {headerNumber}
        </Title>
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          {directionLine}
        </Text>
        <div style={{ marginTop: 6 }}>
          <StatusCell status={application.status} />
        </div>
        <Space wrap size={6} style={{ marginTop: 6 }}>
          {overdueFinanceInContext ? <Tag color="error">Просрочены фин. задачи</Tag> : null}
          {overdueOperationalInContext ? <Tag color="warning">Просрочены опер. задачи</Tag> : null}
        </Space>
      </div>

      <Descriptions size="small" column={1} bordered labelStyle={{ width: 130 }}>
        <Descriptions.Item label="Клиент">{application.client?.name?.trim() || '—'}</Descriptions.Item>
      </Descriptions>

      <div style={{ marginBottom: 8 }}>
        <FinanceSourceDataHint domain="application" />
      </div>
      <Descriptions size="small" column={1} bordered title="Расчёты с клиентом" labelStyle={{ width: 150 }}>
        <Descriptions.Item label="Стоимость перевозки">
          {clientSettlement.transportCost != null ? formatRub(clientSettlement.transportCost) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Получено">{formatRub(clientSettlement.received)}</Descriptions.Item>
        <Descriptions.Item label="Остаток">{formatRub(clientSettlement.remaining)}</Descriptions.Item>
        <Descriptions.Item label="Статус оплаты">{clientSettlement.paymentStatus}</Descriptions.Item>
      </Descriptions>

      {clientObligations.length > 0 ? (
        <div>
          <Text strong style={{ fontSize: 13 }}>
            Обязательства клиента
          </Text>
          <div style={{ marginTop: 8 }}>
            {clientObligations.map((obl) => (
              <ObligationPaymentsBlock
                key={obl.id}
                obligation={mapApplicationIncomeObligationToBlockItem(obl)}
                visible
                moneyType="income"
                readOnly
                compact
                embeddedPaymentRows={projectObligationClientPayments(obl)}
                onAddPayment={() => {}}
              />
            ))}
          </div>
        </div>
      ) : null}

      {linkedRoutes.length > 0 ? (
        <div>
          <Text strong style={{ fontSize: 13 }}>
            Связанные маршруты
          </Text>
          <Space direction="vertical" size={10} style={{ width: '100%', marginTop: 8 }}>
            {linkedRoutes.map((row) => (
              <div
                key={row.linkId}
                style={{ padding: '8px 10px', border: '1px solid #f0f0f0', borderRadius: 6, fontSize: 12 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <Text strong>Маршрут {row.label}</Text>
                  {onSwitchToRoute ? (
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onSwitchToRoute(row.routeId)}>
                      Контекст маршрута
                    </Button>
                  ) : onOpenRoute ? (
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onOpenRoute(row.routeId)}>
                      Открыть маршрут
                    </Button>
                  ) : null}
                </div>
                <Text style={{ display: 'block', marginTop: 4 }}>
                  Направление: {row.direction}
                </Text>
                <Text style={{ display: 'block' }}>
                  Перевозчик: {row.carrierName || '—'}
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Text style={{ fontSize: 12, marginRight: 6 }}>Статус:</Text>
                  <StatusCell status={row.status} />
                </div>
                <Text style={{ display: 'block' }}>
                  Ставка: {row.rateAmount != null ? formatRub(row.rateAmount) : '—'}
                </Text>
                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                  План погрузки: {row.plannedLoadingDate ?? '—'}
                </Text>
                <Text type="secondary" style={{ display: 'block' }}>
                  План выгрузки: {row.plannedUnloadingDate ?? '—'}
                </Text>
                <Text type="secondary" style={{ display: 'block' }}>
                  Факт погрузки: {row.actualLoadingDate ?? '—'}
                </Text>
                <Text type="secondary" style={{ display: 'block' }}>
                  Факт выгрузки: {row.actualUnloadingDate ?? '—'}
                </Text>
              </div>
            ))}
          </Space>
        </div>
      ) : null}

      <Collapse
        size="small"
        defaultActiveKey={['operational', 'finance']}
        items={[
          {
            key: 'operational',
            label: `Операционные задачи (${drawerOperationalTasks.length})`,
            children:
              drawerOperationalTasks.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет открытых опер. задач в очереди" />
              ) : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {drawerOperationalTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      workspaceTask={task}
                      presentationContext={taskPresentationContext}
                      id={task.id}
                      taskType={task.taskType}
                      status={task.status}
                      compact
                      dueDate={task.dueDate}
                      routeId={task.routeId}
                      applicationId={task.applicationId}
                      entityType={task.entityType}
                      invoiceId={task.invoiceId}
                      obligationId={task.obligationId}
                      obligationStage={task.obligationStage}
                      routeStatus={task.routeStatus}
                      loadingCity={task.loadingCity}
                      unloadingCity={task.unloadingCity}
                      carrierName={task.carrierName}
                      onAction={(t) => {
                        onInlineTaskAction(t);
                        onClose();
                      }}
                    />
                  ))}
                </Space>
              ),
          },
          {
            key: 'finance',
            label: `Финансовые задачи (${drawerFinanceTasks.length})`,
            children:
              drawerFinanceTasks.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет открытых фин. задач в очереди" />
              ) : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {drawerFinanceTasks.map((task) => {
                    const amount = resolveApplicationFinanceTaskAmount(task, application);
                    return (
                      <div key={task.id}>
                        {amount != null ? (
                          <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                            Сумма: {formatRub(amount)}
                          </Text>
                        ) : null}
                        <TaskCard
                          workspaceTask={task}
                          presentationContext={taskPresentationContext}
                          id={task.id}
                          taskType={task.taskType}
                          status={task.status}
                          compact
                          dueDate={task.dueDate}
                          routeId={task.routeId}
                          applicationId={task.applicationId}
                          entityType={task.entityType}
                          invoiceId={task.invoiceId}
                          obligationId={task.obligationId}
                          obligationStage={task.obligationStage}
                          routeStatus={task.routeStatus}
                          loadingCity={task.loadingCity}
                          unloadingCity={task.unloadingCity}
                          carrierName={task.carrierName}
                          onAction={(t) => {
                            onInlineTaskAction(t);
                            onClose();
                          }}
                        />
                      </div>
                    );
                  })}
                </Space>
              ),
          },
        ]}
      />

      <Button type="link" size="small" onClick={onOpenFullCard} style={{ paddingLeft: 0 }}>
        Открыть полную карточку…
      </Button>
    </Space>
  );
};

export default ApplicationContextDrawerPanel;